import React, { useState, useEffect, useRef } from 'react';
import { GlassCard } from '../../_shared/GlassCard';
import { Eye, Users, Flame } from 'lucide-react';

const useCounter = (end, duration = 1400) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    const start = performance.now();
                    const animate = (now) => {
                        const progress = Math.min(1, (now - start) / duration);
                        const eased = 1 - Math.pow(1 - progress, 3);
                        setCount(Math.floor(end * eased));
                        if (progress < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                }
            },
            { threshold: 0.3 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);

    return [count, ref];
};

const KPICard = ({ icon: Icon, iconColor, label, value, suffix = '', subtext }) => {
    const [animatedValue, ref] = useCounter(value);

    return (
        <GlassCard className="flex flex-col justify-between min-h-[180px]">
            <div ref={ref}>
                <div className={`${iconColor} p-3 rounded-xl mb-4 inline-flex`}>
                    <Icon size={22} />
                </div>
                <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-2">{label}</p>
                <p className="text-4xl font-black text-white tracking-tight">
                    {animatedValue.toLocaleString()}{suffix}
                </p>
                <p className="text-white/50 text-sm mt-3">{subtext}</p>
            </div>
        </GlassCard>
    );
};

export const HeroSection = () => {
    return (
        <section className="min-h-[85vh] flex flex-col justify-center items-center text-center px-4 pt-24 pb-12 relative z-10">
            <div className="space-y-6 max-w-5xl mx-auto w-full">

                {/* Badge */}
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 backdrop-blur-md mb-2">
                    <span className="text-sm font-medium text-yellow-300 tracking-wide uppercase">Jan – Mar 2026 · 90 Days</span>
                </div>

                {/* Headline */}
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-yellow-100 to-white drop-shadow-lg leading-tight">
                    Kush 90-Day<br />Growth Report
                </h1>

                <p className="text-lg md:text-xl text-yellow-200/70 max-w-2xl mx-auto leading-relaxed">
                    Top-performing reels across <span className="text-white font-semibold">three months</span> of strategic content marketing — the full trajectory from cold start to viral authority.
                </p>

                {/* KPI Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-12 w-full">
                    <KPICard
                        icon={Eye}
                        iconColor="bg-yellow-500/20 text-yellow-300"
                        label="Total Views"
                        value={4290000}
                        suffix=""
                        subtext="+96.8% from Non-followers"
                    />
                    <KPICard
                        icon={Users}
                        iconColor="bg-purple-500/20 text-purple-300"
                        label="Followers Gained"
                        value={17600}
                        suffix=""
                        subtext="Start: 3,879 → Current: 21,563"
                    />
                    <KPICard
                        icon={Flame}
                        iconColor="bg-red-500/20 text-red-300"
                        label="Viral Hit Reels"
                        value={8}
                        suffix=""
                        subtext=">100K+ Views generated"
                    />
                </div>
            </div>
        </section>
    );
};
